// src/api/statusApi.js
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(
      "Making API request:",
      config.method?.toUpperCase(),
      config.url
    );
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log("API response received:", response.status, response.data);
    return response;
  },
  (error) => {
    console.error("Response error:", error.response?.data || error.message);
    return Promise.reject(error);
  }
);

/**
 * Create a tuition payment link
 * @param {Object} paymentData - The payment data object
 * @returns {Promise<Object>} - Payment response with checkout URL and reference number
 */
export const createTuitionPayment = async (paymentData) => {
  try {
    // Validate required fields before sending
    if (
      !paymentData.studentInfo?.email ||
      !paymentData.studentInfo?.studentID
    ) {
      throw new Error("Student information (email and studentID) is required");
    }

    if (!paymentData.paymentAmount || paymentData.paymentAmount <= 0) {
      throw new Error("Valid payment amount is required");
    }

    if (!paymentData.agreeToTerms) {
      throw new Error("Must agree to terms and conditions");
    }

    console.log(
      "Creating tuition payment for student:",
      paymentData.studentInfo.studentID
    );
    console.log("Payment amount:", paymentData.paymentAmount);
    console.log("Payment type:", paymentData.paymentType);

    const response = await apiClient.post("/api/enrollee-applicants-v2/payment-tuition", paymentData);

    if (response.data.success) {
      console.log(
        "Payment link created successfully:",
        response.data.data.referenceNumber
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } else {
      throw new Error(response.data.error || "Failed to create payment link");
    }
  } catch (error) {
    console.error("Error creating tuition payment:", error);

    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const errorMessage =
        error.response.data?.error || "Server error occurred";
      const statusCode = error.response.status;

      throw new Error(`${errorMessage} (Status: ${statusCode})`);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error(
        "No response from server. Please check your internet connection."
      );
    } else {
      // Something else happened
      throw new Error(error.message || "An unexpected error occurred");
    }
  }
};

/**
 * Get payment status by reference number
 * @param {string} referenceNumber - The payment reference number
 * @returns {Promise<Object>} - Payment status information
 */
export const getPaymentStatus = async (referenceNumber) => {
  try {
    if (!referenceNumber) {
      throw new Error("Reference number is required");
    }

    const response = await apiClient.get(`/payment-status/${referenceNumber}`);
    return response.data;
  } catch (error) {
    console.error("Error getting payment status:", error);
    throw error;
  }
};

/**
 * Get student payment history
 * @param {string} studentID - The student ID
 * @param {string} email - The student email
 * @returns {Promise<Object>} - Payment history
 */
export const getStudentPaymentHistory = async (studentID, email) => {
  try {
    if (!studentID || !email) {
      throw new Error("Student ID and email are required");
    }

    const response = await apiClient.get(
      `/payment-history/${studentID}?email=${encodeURIComponent(email)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error getting payment history:", error);
    throw error;
  }
};

/**
 * Cancel a pending payment
 * @param {string} referenceNumber - The payment reference number
 * @param {string} studentID - The student ID
 * @returns {Promise<Object>} - Cancellation result
 */
export const cancelPayment = async (referenceNumber, studentID) => {
  try {
    if (!referenceNumber || !studentID) {
      throw new Error("Reference number and student ID are required");
    }

    const response = await apiClient.post("/cancel-payment", {
      referenceNumber,
      studentID,
    });

    return response.data;
  } catch (error) {
    console.error("Error cancelling payment:", error);
    throw error;
  }
};

// Example usage function - you can remove this if not needed
export const exampleUsage = async () => {
  const samplePaymentData = {
    agreeToTerms: true,
    installmentPlan: [
      {
        amount: 5930.26,
        dueDate: "Upon Enrollment",
        type: "Downpayment (1/3)",
      },
      {
        amount: 5930.26,
        dueDate: "Midterm Period",
        type: "Midterm Payment (1/3)",
      },
      {
        amount: 5930.26,
        dueDate: "Finals Period",
        type: "Finals Payment (1/3)",
      },
      { amount: 17790.78, dueDate: "", type: "TOTAL" },
    ],
    isFullPayment: false,
    paymentAmount: 5930.26,
    paymentMethod: "gcash",
    paymentMode: "Installment Plan",
    paymentType: "downpayment",
    remainingPayments: [
      {
        amount: 5930.26,
        dueDate: "Midterm Period",
        type: "Midterm Payment (1/3)",
      },
      {
        amount: 5930.26,
        dueDate: "Finals Period",
        type: "Finals Payment (1/3)",
      },
      { amount: 17790.78, dueDate: "", type: "TOTAL" },
    ],
    studentInfo: {
      academicLevel: "Grade 11",
      academicStrand: "HUMSS",
      academicTerm: "1st",
      academicYear: "2025-2026",
      dob: "2004-07-15T00:00:00.000Z",
      email: "josephlouisedeleon22@gmail.com",
      fullName: "joseph louise centeno de leon",
      gender: "male",
      hasReservation: false,
      mobile: "+639943936541",
      studentID: "S20254032",
      voucherType: "PRIVATE SCHOOL WITHOUT VOUCHER",
    },
    timestamp: new Date().toISOString(),
    tuitionAndFees: {
      energyFee: 0,
      miscellaneous: 1075,
      otherFees: 3508.75,
      semester: "1st_sem",
      totalAmount: 16783.75,
      tuition: 12200,
      voucherDeduction: 0,
      voucherType: "PRIVATE SCHOOL WITHOUT VOUCHER",
    },
  };

  try {
    const result = await createTuitionPayment(samplePaymentData);
    console.log("Payment created:", result);

    // Redirect user to checkout URL
    if (result.success && result.data.checkoutUrl) {
      window.location.href = result.data.checkoutUrl;
    }
  } catch (error) {
    console.error("Payment creation failed:", error.message);
    //
    // Handle error in your UI
  }
};
/**

 * Update enrollment approval status of an applicant.
 * @param {string} applicantId - The ID of the applicant to update.
 * @param {"Approve"|"Reject"} status - Status to update (Approve => Complete, Reject => Rejected).
 * @param {string} [rejectMessage] - Optional rejection reason when status is Reject.
 * @returns {Promise<object>} - API response with updated applicant data.
 */
export const updateStatusOfRequirmentsAdminApiRequest = async (
  applicantId,
  status,
  rejectMessage = ""
) => {
  try {
    const payload = { applicantId, status };
    console.log("pas2", payload);
    console.log("status", status);
    if (status === "Rejected") {
      payload.rejectMessage = rejectMessage;
    }

    console.log("payss", payload);

    const response = await axios.patch(
      `${API_URL}/api/enrollee-applicants-v2/update-status-of-requirments-admin`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("API request failed:", error);
    throw (
      error.response?.data || {
        success: false,
        message: "Unknown error occurred",
      }
    );
  }
};

export const savedVoucherApplicationApiRequest = async (data) => {
  try {
    const formData = new FormData();

    // Append standard fields
    formData.append("email", data.email);
    formData.append("voucherType", data.voucherType);

    // Append the requirements array (as JSON string)
    formData.append("requirements", JSON.stringify(data.requirements));

    // Append each file (make sure each file object has an `id` to match requirement ID)
    if (data.files && Array.isArray(data.files)) {
      data.files.forEach((fileObj) => {
        if (fileObj.file && fileObj.requirementId) {
          // Append using the same naming format as in the backend
          formData.append(`file-${fileObj.requirementId}`, fileObj.file);
        }
      });
    }

    const response = await axios.post(
      `${API_URL}/api/enrollee-applicants/save-voucher-application`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error in savedVoucherApplicationApiRequest:", error);
    if (error.response) {
      throw new Error(
        error.response.data?.error || "Failed to save voucher application"
      );
    }
    throw new Error("Network error while saving voucher application");
  }
};

export const updateStatusOfAdmissionRequirementsApiRequest = async (
  applicantId,
  status
) => {
  try {
    console.log("staaa", status);
    console.log("2221", applicantId);
    const response = await axios.patch(
      `${API_URL}/api/enrollee-applicants-v2/update-admission-requirement-status`,
      {
        applicantId,
        status,
      }
    );
    console.log("resss", response);
    return response.data;
  } catch (error) {
    console.error("Failed to update admission status:", error);
    throw error.response?.data || { message: "Unknown error occurred" };
  }
};

export const updateStatusOfEnrollmentRequirementsApiRequest = async (
  applicantId,
  status
) => {
  try {
    const response = await axios.patch(
      `${API_URL}/api/enrollee-applicants-v2/update-enrollment-requirement-status`,
      {
        applicantId,
        status,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update enrollment status:", error);
    throw error.response?.data || { message: "Unknown error occurred" };
  }
};

export const getAllApplicantAdmissionRequirementsStatusApiRequest = async (
  page = 1,
  limit = 10,
  filters = {}
) => {
  try {
    // Always filter for incomplete admission requirements status
    const params = {
      page,
      limit,
      admissionRequirementsStatus: "Incomplete",
      ...filters,
    };

    const response = await axios.get(
      `${API_URL}/api/enrollee-applicants-v2/applicants-admissionRequirementsStatus-incomplete`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching applicant admission requirements status:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

export const getApplicantByEmailApiRequest = async (email) => {
  try {
    if (!email) {
      throw new Error("Email is required");
    }

    const response = await axios.get(
      `${API_URL}/api/enrollee-applicants-v2/applicant/${email}`
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching applicant details:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Update applicant's admission requirements status
 * @param {string} applicantId - The ID of the applicant
 * @param {string} status - The new status (Approved, Rejected, etc.)
 * @param {string} [rejectReason] - Optional reason for rejection if status is Rejected
 * @returns {Promise<Object>} - API response with updated status
 */
export const updateApplicantAdmissionRequirementsStatusApiRequest = async (
  applicantId,
  status,
  rejectReason = null
) => {
  try {
    if (!applicantId) {
      throw new Error("Applicant ID is required");
    }

    if (!status) {
      throw new Error("Status is required");
    }

    const data = {
      status,
      ...(rejectReason && { rejectReason }),
    };

    const response = await axios.put(
      `${API_URL}/api/enrollee-applicants-v2/applicant/${applicantId}/admission-requirements-status`,
      data
    );

    return response.data;
  } catch (error) {
    console.error(
      "Error updating applicant admission requirements status:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Save enrollment requirements to the server
 * @param {Object} data - Enrollment data object containing requirements
 * @returns {Promise<Object>} Response data from the server
 */
export const saveEnrollmentRequirementsFormApiRequest = async (data) => {
  try {
    // Validate input data
    if (!data || !data.email) {
      throw new Error("Email is required");
    }

    if (
      !data.requirements ||
      !Array.isArray(data.requirements) ||
      data.requirements.length === 0
    ) {
      throw new Error("Requirements data is required");
    }

    // Create FormData for file uploads
    const formData = new FormData();

    // Add basic fields
    formData.append("email", data.email);

    // Process each requirement
    const requirementsForJson = data.requirements.map((req) => {
      const { id, name, status, waived, waiverDetails } = req;

      // Only include essential metadata in JSON
      const reqCopy = { id, name, status, waived };

      if (waiverDetails) {
        reqCopy.waiverDetails = waiverDetails;
      }

      // Process file if exists and not waived
      if (req.file && !waived) {
        // Extract file data
        if (req.file.fileData || req.file.fileDataFull) {
          const fileData = req.file.fileDataFull || req.file.fileData;
          const fileName = req.file.fileName || `file-${id}`;
          const fileType = req.file.fileType || "application/octet-stream";

          // Convert base64 to file if needed
          if (typeof fileData === "string" && fileData.includes("base64")) {
            const base64Content = fileData.split("base64,")[1];
            const byteCharacters = atob(base64Content);
            const byteArrays = [];

            for (let i = 0; i < byteCharacters.length; i += 1024) {
              const slice = byteCharacters.slice(i, i + 1024);
              const byteNumbers = new Array(slice.length);

              for (let j = 0; j < slice.length; j++) {
                byteNumbers[j] = slice.charCodeAt(j);
              }

              byteArrays.push(new Uint8Array(byteNumbers));
            }

            const blob = new Blob(byteArrays, { type: fileType });
            const file = new File([blob], fileName, { type: fileType });

            // Append to form with requirement ID reference
            formData.append(`file-${id}`, file);
          }
        }
      }

      return reqCopy;
    });

    // Add requirements JSON data
    formData.append("requirementsData", JSON.stringify(requirementsForJson));

    // Optional fields
    if (data.applicantId) formData.append("applicantId", data.applicantId);
    if (data.studentId) formData.append("studentId", data.studentId);
    if (data.submissionComplete)
      formData.append("submissionComplete", data.submissionComplete);

    // Make API request
    const response = await axios.post(
      `${API_URL}/api/enrollee-applicants/save-enrollment-requirements`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000, // 30 second timeout
      }
    );

    return response.data;
  } catch (error) {
    // Enhanced error handling
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Unknown error occurred";

    console.error("Failed to save enrollment requirements:", errorMessage);

    // Rethrow with clear message
    throw new Error(
      `Enrollment requirements submission failed: ${errorMessage}`
    );
  }
};

/**
 * Get all files uploaded from Cloudinary for a specific applicant
 * @param {string} applicantId - MongoDB ObjectId of the applicant
 * @returns {Promise<Object>} - API response with file data
 */
export const getAllFilesUploadedFromCloudinaryByApplicantId = async (
  applicantId
) => {
  try {
    if (!applicantId || applicantId.length !== 24) {
      throw new Error("Invalid or missing applicant ID");
    }

    const response = await axios.get(
      `${API_URL}/api/enrollee-applicants/get-file-uploaded-in-cloudinary/${applicantId}`
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching files from Cloudinary:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Update reservation applicant's admission approval status.
 *
 * @param {string} applicantId - The ID of the applicant.
 * @param {"Approved" | "Rejected"} status - The status to set.
 * @param {string} [rejectMessage] - Optional rejection reason if status is "Rejected".
 * @returns {Promise<Object>} - API response data.
 */
export const updateApplicantPaymentStatusApiRequest = async (
  applicantId,
  status,
  rejectMessage = null
) => {
  try {
    const payload = {
      applicantId,
      status,
      ...(status === "Rejected" && { rejectMessage }),
    };

    const response = await axios.patch(
      `${API_URL}/api/enrollee-applicants/update-status-of-reservation-applicant`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error("API error:", error);
    throw error.response?.data || { error: "Unknown API error" };
  }
};

/**
 * Send a forgot password request to initiate the OTP process
 * @param {string} email - The user's email address
 * @returns {Promise<Object>} - The API response
 */
export const forgotPasswordApplicantApiRequest = async (email) => {
  try {
    if (!email) {
      throw new Error("Email is required");
    }

    // Validate email format on client side as well
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error("Invalid email format");
    }
    console.log("Email format is valid", email);
    const response = await axios.post(
      `${API_URL}/api/enrollee-applicants/forgot-password/applicant`,
      {
        email,
      }
    );
    console.log("response", response);
    return {
      success: true,
      message: response.data.message,
      data: response.data,
    };
  } catch (error) {
    console.error("Error in forgotPasswordApplicantApiRequest:", error);
    // Handle different types of errors
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      return {
        success: false,
        message: error.response.data.message || "Password reset request failed",
        error: error.response.data,
      };
    } else if (error.request) {
      // The request was made but no response was received
      return {
        success: false,
        message: "No response from server. Please check your connection",
        error: error.request,
      };
    } else {
      // Something happened in setting up the request that triggered an Error
      return {
        success: false,
        message: error.message || "Password reset request failed",
        error: error,
      };
    }
  }
};

/**
 * Verify OTP code for password reset
 * @param {string} email - The user's email address
 * @param {string} otp - The 6-digit OTP code
 * @returns {Promise<Object>} - API response with verification status
 */
export const verifyResetPasswordOtp = async (email, otp) => {
  try {
    // Input validation
    if (!email || !otp) {
      throw new Error("Email and OTP are required");
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error("Invalid email format");
    }

    if (!/^\d{6}$/.test(otp)) {
      throw new Error("OTP must be a 6-digit number");
    }

    // Call API to verify OTP
    const response = await axios.post(
      `${API_URL}/api/enrollee-applicants/verify-reset-password-otp`,
      { email, otp }
    );

    return {
      success: true,
      message: response.data.message || "OTP verified successfully",
      data: response.data,
    };
  } catch (error) {
    console.error("Error in verifyResetPasswordOtp:", error);
    if (error.response) {
      return {
        success: false,
        message: error.response.data.message || "OTP verification failed",
        error: error.response.data,
      };
    } else if (error.request) {
      return {
        success: false,
        message: "No response from server. Please check your connection.",
        error: error.request,
      };
    } else {
      return {
        success: false,
        message: error.message || "OTP verification failed",
        error,
      };
    }
  }
};

/**
 * Reset password after OTP verification
 * @param {string} email - The user's email address
 * @param {string} newPassword - The new password
 * @returns {Promise<Object>} - API response with reset status
 */
export const resetApplicantPassword = async (email, newPassword) => {
  try {
    // Basic input validation
    if (!email || !newPassword) {
      throw new Error("Email and new password are required");
    }

    // Call API to reset password
    const response = await axios.post(
      `${API_URL}/api/enrollee-applicants/reset-password/applicant`,
      { email, newPassword }
    );

    // The backend returns success: true/false in the response
    return {
      success: response.data.success,
      message: response.data.message || "Password reset successful",
      data: response.data,
    };
  } catch (error) {
    console.error("Error in resetApplicantPassword:", error);
    if (error.response) {
      // Return the error response from the server
      return {
        success: false,
        message: error.response.data.message || "Password reset failed",
        error: error.response.data,
      };
    } else if (error.request) {
      // The request was made but no response was received
      return {
        success: false,
        message: "No response from server. Please check your connection.",
        error: error.request,
      };
    } else {
      // Something happened in setting up the request
      return {
        success: false,
        message: error.message || "Password reset failed",
        error,
      };
    }
  }
};

/**
 * Complete two-step password reset process: verify OTP then reset password
 * @param {string} email - The user's email address
 * @param {string} otp - The OTP received via email
 * @param {string} newPassword - The new password (optional for first step)
 * @returns {Promise<Object>} - Combined result of verification and reset
 */
export const verifyOtpAndResetPasswordApiRequest = async (
  email,
  otp,
  newPassword
) => {
  try {
    // If no password provided, just verify the OTP
    if (!newPassword) {
      return await verifyResetPasswordOtp(email, otp);
    }

    // Step 1: Verify OTP
    const verifyResult = await verifyResetPasswordOtp(email, otp);
    if (!verifyResult.success) {
      return verifyResult; // Return the error from verification
    }

    // Step 2: Reset Password
    const resetResult = await resetApplicantPassword(email, newPassword);
    return resetResult;
  } catch (error) {
    console.error("Error in verifyOtpAndResetPasswordApiRequest:", error);
    return {
      success: false,
      message: error.message || "Failed to complete password reset process",
      error,
    };
  }
};

// For backward compatibility
export const verifyOtpCodeForgotPasswordApiRequest = verifyResetPasswordOtp;

/**
 * Updates an applicant's interview and/or exam status
 * @param {string} applicantId - The ID of the applicant to update
 * @param {Object} statusData - Object containing interview and/or exam status
 * @param {string} [statusData.interviewStatus] - New interview status (Pending, Scheduled, Completed, Passed, Failed)
 * @param {string} [statusData.examStatus] - New exam status (Pending, Scheduled, Completed, Passed, Failed)
 * @returns {Promise<Object>} - The response data with updated status information
 */
export const updateApplicantStatusResult = async (applicantId, statusData) => {
  try {
    if (!applicantId) {
      throw new Error("Applicant ID is required");
    }

    if (
      !statusData ||
      (!statusData.interviewStatus && !statusData.examStatus)
    ) {
      throw new Error(
        "At least one status field (interviewStatus or examStatus) is required"
      );
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    const response = await axios.patch(
      `${API_URL}/api/enrollee-applicants/update-status/results/${applicantId}`,
      { statusData },
      config
    );

    return response.data;
  } catch (error) {
    console.log("Error updating applicant status:", error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to update applicant status";

    console.error("Error updating applicant status:", errorMessage);
    throw new Error(errorMessage);
  }
};

export const getAllApplicationsApprovedExamAndInterviewSchedule = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/api/enrollee-applicants/applicants/approved`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching approved applicants:", error);
    return {
      success: false,
      error:
        error.response?.data?.error || "Failed to fetch approved applicants",
    };
  }
};
/**
 * Updates an applicant's exam and interview schedule data
 * @param {string} applicantId - The ID of the applicant
 * @param {Object} scheduleData - The schedule data to update
 * @returns {Promise} - Promise resolving to the API response data
 */
export const updateApplicantExamAndScheduleDataApiRequest = async (
  applicantId,
  scheduleData
) => {
  console.log("Updating exam schedule with data:", {
    applicantId,
    scheduleData,
  });

  try {
    const response = await axios.patch(
      `${API_URL}/api/enrollee-applicants/update-interview-and-exam-data`,
      { applicantId, scheduleData },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );

    console.log("API response:", response.data);
    return response.data;
  } catch (error) {
    // Enhanced error handling
    if (error.response) {
      // The server responded with a status code outside the 2xx range
      console.error("API Error Response:", {
        status: error.response.status,
        data: error.response.data,
        endpoint: `${API_URL}/api/enrollee-applicants/update-interview-and-exam-data`,
        requestBody: { applicantId, scheduleData },
      });

      // Return a standardized error object instead of throwing
      return {
        success: false,
        error:
          error.response.data.message ||
          error.response.data.error ||
          "Server error occurred",
      };
    } else if (error.request) {
      // The request was made but no response was received
      console.error("No response received:", {
        request: error.request,
        endpoint: `${API_URL}/api/enrollee-applicants/update-interview-and-exam-data`,
        requestBody: { applicantId, scheduleData },
      });
      return {
        success: false,
        error: "No response received from server",
      };
    } else {
      // Something happened in setting up the request
      console.error("Request Error:", {
        message: error.message,
        endpoint: `${API_URL}/api/enrollee-applicants/update-interview-and-exam-data`,
        requestBody: { applicantId, scheduleData },
      });
      return {
        success: false,
        error: `Request failed: ${error.message}`,
      };
    }
  }
};

/**
 * Get all applicants with "On-going" status with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Number of items per page (default: 10)
 * @returns {Promise<Object>} - Promise containing the response data
 */
export const getAllApplicantsOngoingExamAndInterviewSchedule = async (
  params = {}
) => {
  try {
    // Set default pagination values if not provided
    const page = params.page || 1;
    const limit = params.limit || 10;

    // Make API request with query parameters
    const response = await axios.get(
      `${API_URL}/api/enrollee-applicants/applicants/ongoing`,
      {
        params: {
          page,
          limit,
        },
      }
    );

    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    console.error("Error fetching ongoing applicants:", error);

    // Return structured error response
    return {
      success: false,
      error:
        error.response?.data?.error || "Failed to fetch ongoing applicants",
    };
  }
};

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 10000,
});

// Request interceptor for adding common headers
axiosInstance.interceptors.request.use(
  (config) => {
    // You could add authentication headers here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for common error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error("Server error:", error.response.data);
    } else if (error.request) {
      // Request made but no response received
      console.error("Network error - no response received");
    } else {
      // Error occurred during request setup
      console.error("Request error:", error.message);
    }
    return Promise.reject(error);
  }
);

// Reusable fetch function with retry logic
const fetchWithRetry = async (
  endpoint,
  options = {},
  retries = 3,
  delay = 1000
) => {
  let lastError;

  for (let i = 0; i < retries; i++) {
    try {
      const response = await axiosInstance({
        url: endpoint,
        ...options,
      });
      return response.data;
    } catch (error) {
      lastError = error;
      if (i === retries - 1) break;

      // Exponential backoff delay
      await new Promise((resolve) =>
        setTimeout(resolve, delay * Math.pow(2, i))
      );
    }
  }

  throw lastError;
};

export const verifyUser = async (email) => {
  return fetchWithRetry(
    `/api/enrollee-applicants/verification-status/${email}`
  );
};

export const getUserData = async (email, createdAt) => {
  return fetchWithRetry(
    `/api/enrollee-applicants/activity/${email}?createdAt=${encodeURIComponent(
      createdAt
    )}`
  );
};

export const getPersonalDetails = async (email) => {
  return fetchWithRetry(`/api/enrollee-applicants/personal-details/${email}`);
};

export const getAdmissionRequirements = async (email) => {
  return fetchWithRetry(
    `/api/enrollee-applicants/admission-requirements/${email}`
  );
};

export const getExamInterviewData = async (email) => {
  return fetchWithRetry(`/api/enrollee-applicants/exam-interview/${email}`);
};

export const getExamDetailsData = async (email) => {
  return fetchWithRetry(`/api/enrollee-applicants/exam-details/${email}`);
};

export const getEnrollmentRequirements = async (email) => {
  return fetchWithRetry(
    `/api/enrollee-applicants/enrollment-requirements/${email}`
  );
};

export const getVoucherApplication = async (email) => {
  return fetchWithRetry(
    `/api/enrollee-applicants/voucher-application/${email}`
  );
};

export const updateEnrollmentApprovalStatus = async (email, status) => {
  return fetchWithRetry(
    `/api/enrollment-approval/update-enrollment-approval-status`,
    {
      method: "POST",
      data: { email, enrollmentApprovalStatus: status },
    }
  );
};

export const logoutUser = async (email, createdAt) => {
  return fetchWithRetry(`/api/enrollee-applicants/logout`, {
    method: "POST",
    data: { email, createdAt },
  });
};
